import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { getStats, getActiveVisitors, checkOut, checkOutAll, Visitor, getErrorMessage } from '../visitorKioskApi';

// Fontana brand color
const FONTANA_BLUE = '#1a365d';

const POLL_INTERVAL = 15000;

interface OverviewTabProps {
  onCountChange?: (count: number) => void;
}

export default function OverviewTab({ onCountChange }: OverviewTabProps) {
  const [count, setCount] = useState<number | null>(null);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Checkout state
  const [checkingOutId, setCheckingOutId] = useState<string | null>(null);
  const [checkingOutAll, setCheckingOutAll] = useState(false);
  const [showCheckOutAllDialog, setShowCheckOutAllDialog] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsResult, activeResult] = await Promise.all([
        getStats(),
        getActiveVisitors(100),
      ]);
      setCount(statsResult.inBuildingCount);
      setVisitors(activeResult.visitors);
      setError(null);
      onCountChange?.(statsResult.inBuildingCount);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const formatCheckInTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  // Handle single visitor checkout
  const handleCheckOut = async (visitId: string) => {
    setCheckingOutId(visitId);
    try {
      await checkOut(visitId);
      // Refresh data after checkout
      await fetchData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCheckingOutId(null);
    }
  };

  // Handle check out all
  const handleCheckOutAll = async () => {
    setShowCheckOutAllDialog(false);
    setCheckingOutAll(true);
    try {
      await checkOutAll();
      // Refresh data after checkout
      await fetchData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCheckingOutAll(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}

      {/* Brandsäkerhet Card */}
      <Card 
        sx={{ 
          bgcolor: '#FFF8E1',
          border: '1px solid #FFE082',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, py: 3, px: 4 }}>
          <Avatar sx={{ bgcolor: '#FFB300', width: 56, height: 56 }}>
            <ShieldIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#5D4037' }}>
              Brandsäkerhet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aktuell beläggning i byggnaden
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h2" component="div" sx={{ fontWeight: 700, color: '#F57C00', lineHeight: 1 }}>
              {count ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Besökare i byggnaden
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Aktuella besökare */}
      <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ pb: 1, px: 3, pt: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <GroupsIcon sx={{ color: 'text.secondary', fontSize: 26 }} />
              <Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  Aktuella besökare
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Personer som är incheckade i byggnaden
                </Typography>
              </Box>
            </Box>
            {visitors.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={checkingOutAll ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon />}
                onClick={() => setShowCheckOutAllDialog(true)}
                disabled={checkingOutAll}
                sx={{ 
                  borderRadius: 2,
                  minHeight: 40,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Checka ut alla
              </Button>
            )}
          </Box>
        </CardContent>
        
        {visitors.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <GroupsIcon sx={{ fontSize: 56, color: 'grey.300', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Inga besökare i byggnaden just nu
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 300, overflow: 'auto' }}>
            {visitors.map((visitor, index) => (
              <React.Fragment key={visitor._id}>
                {index > 0 && <Divider />}
                <ListItem 
                  sx={{ py: 2, px: 3 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleCheckOut(visitor._id)}
                      disabled={checkingOutId === visitor._id}
                      sx={{
                        bgcolor: '#FFF3E0',
                        color: '#E65100',
                        '&:hover': { bgcolor: '#FFE0B2' },
                        '&.Mui-disabled': { bgcolor: '#F5F5F5' },
                      }}
                      title="Checka ut"
                    >
                      {checkingOutId === visitor._id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <LogoutIcon />
                      )}
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#E8EEF4', width: 44, height: 44 }}>
                      <PersonIcon sx={{ color: FONTANA_BLUE }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {visitor.fullName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {visitor.company && `${visitor.company} • `}
                        {visitor.visitReason}
                        {visitor.hostName && ` • Träffar: ${visitor.hostName}`}
                        {` • Kl ${formatCheckInTime(visitor.checkInAt)}`}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>

      {/* Check out all confirmation dialog */}
      <Dialog
        open={showCheckOutAllDialog}
        onClose={() => setShowCheckOutAllDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Checka ut alla besökare?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Detta kommer att checka ut <strong>{visitors.length}</strong> besökare från byggnaden.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setShowCheckOutAllDialog(false)}
            variant="outlined"
            size="large"
            sx={{ minHeight: 48, borderRadius: 2 }}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleCheckOutAll}
            variant="contained"
            color="error"
            size="large"
            startIcon={<LogoutIcon />}
            sx={{ minHeight: 48, borderRadius: 2 }}
          >
            Checka ut alla
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
